import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayers, queriesPayrollSchedules } from "@query";
import {
  toWSDateString,
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInputNumber,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayrollSchedule,
  PayrollScheduleDayOfWeek,
  PayrollScheduleFrequency,
  PayrollScheduleType,
  RecurrenceScheduleDate
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { RadioGroupWithCustomInputs } from "../../../components/RadioGroupWithCustomInputs/RadioGroupWithCustomInputs";
import {
  PayrollCadence,
  selectorPayrollCadence
} from "../../../shared/selectors/selectorPayrollCadence";
import { PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD } from "../../../modules/Settings/paths";
import { EmptyState } from "../../../shared/components/EmptyState";
import { WeekDay } from "../../../shared/types/time";
import { dayInIntervalToWeekDayMapping } from "../../../shared/utils/time";
import { getExactCutOffDate } from "../../utils/payables";

/**
 * Contractor payroll schedule settings, on v3.
 *
 * Two v3 resources back this screen, and they are deliberately not the same one:
 *
 * - **the cadence and the upcoming runs** live on `PayrollSchedule`
 *   (`/v3/payments/payroll-schedules`), the resource v3 designates for both. It
 *   is addressed by type rather than by id, and projects `scheduleDates`
 *   read-only. `PayerSettings` declares no 1099 `frequency` at all.
 * - **the payroll on/off switch** lives on `PayerSettings`
 *   (`/v3/payments/payer-settings`), which accepts `runsPayroll` on its PATCH.
 *
 * Neither write is confirmed working. The payer-settings PATCH is mounted and
 * validates a well-formed body (an unauthenticated probe reaches 401, while an
 * unmounted verb on the same path still answers 405), so the PLA-3495 write verb
 * has shipped — but whether its handler is implemented cannot be observed from
 * outside, because a 501 is raised behind auth and looks identical to a working
 * endpoint. The cadence resource is documented as 501 on every operation and
 * that is likewise unverifiable without a session.
 *
 * Both are wired to v3 regardless, per the migration rule that a user-triggered
 * action moves even when its endpoint is incomplete: the screen still renders off
 * the live read, and a failed save surfaces at the click through `ErrorAlert`
 * instead of hiding behind a dead v1 call. See V3_MIGRATION_TRACKER.md
 * § payer-settings for the full gap list.
 */

enum Options {
  Monthly = "Monthly",
  SemiMonthly = "SemiMonthly",
  Everyday = "Everyday",
  Week = "Week"
}

type FormData = {
  interval: Options;
  every: number;
  dayInInterval: WeekDay;
};

/**
 * `PayrollScheduleUpdate.interval` takes any N, but the payroll engine is only
 * known to honour weekly and biweekly, so the design's free-number input stays
 * capped rather than letting a payer type 3 and receive a schedule nobody runs.
 * Tracked as gap G-CAD-N.
 */
const MAX_WEEK_INTERVAL = 2;

const WEEK_DAYS = Object.values(WeekDay);

/**
 * `WeekDay` and the `dayInInterval` mappings run Monday-first (1–7), while
 * `Date.getUTCDay()` runs Sunday-first (0–6). Indexing one with the other reads
 * a Friday schedule as Saturday, so every crossing goes through here.
 */
const jsDayToDayInInterval = (jsDay: number) => (jsDay === 0 ? 7 : jsDay);

const weekDayToScheduleDayOfWeek: Record<WeekDay, PayrollScheduleDayOfWeek> = {
  [WeekDay.Monday]: PayrollScheduleDayOfWeek.MONDAY,
  [WeekDay.Tuesday]: PayrollScheduleDayOfWeek.TUESDAY,
  [WeekDay.Wednesday]: PayrollScheduleDayOfWeek.WEDNESDAY,
  [WeekDay.Thursday]: PayrollScheduleDayOfWeek.THURSDAY,
  [WeekDay.Friday]: PayrollScheduleDayOfWeek.FRIDAY,
  [WeekDay.Saturday]: PayrollScheduleDayOfWeek.SATURDAY,
  [WeekDay.Sunday]: PayrollScheduleDayOfWeek.SUNDAY
};

const scheduleDayOfWeekToWeekDay: Record<PayrollScheduleDayOfWeek, WeekDay> = {
  [PayrollScheduleDayOfWeek.MONDAY]: WeekDay.Monday,
  [PayrollScheduleDayOfWeek.TUESDAY]: WeekDay.Tuesday,
  [PayrollScheduleDayOfWeek.WEDNESDAY]: WeekDay.Wednesday,
  [PayrollScheduleDayOfWeek.THURSDAY]: WeekDay.Thursday,
  [PayrollScheduleDayOfWeek.FRIDAY]: WeekDay.Friday,
  [PayrollScheduleDayOfWeek.SATURDAY]: WeekDay.Saturday,
  [PayrollScheduleDayOfWeek.SUNDAY]: WeekDay.Sunday
};

/**
 * The weekday a weekly schedule lands on is `dayOfWeek`. A schedule written
 * before that field existed carries it only in `startDate`, and a schedule the
 * cadence resource will not serve carries it only in the pay dates it produced —
 * so the weekday is read from whichever of the three answers first.
 */
const frequencyToFormData = (
  schedule: PayrollSchedule | undefined,
  /**
   * Fallback for when the cadence resource is unavailable — see
   * {@link selectorPayrollCadence}. The schedule wins when both are present,
   * since it is the stored intent rather than a reading of its output.
   */
  derived?: PayrollCadence
): FormData => {
  const frequency = schedule?.frequency ?? derived?.frequency;
  const every = schedule?.frequency
    ? (schedule.interval ?? 1)
    : (derived?.interval ?? 1);

  const anchorDay =
    schedule?.startDate !== undefined
      ? new Date(schedule.startDate).getUTCDay()
      : derived?.weekday;

  const dayInInterval = schedule?.dayOfWeek
    ? scheduleDayOfWeekToWeekDay[schedule.dayOfWeek]
    : anchorDay === undefined
      ? WeekDay.Monday
      : dayInIntervalToWeekDayMapping[jsDayToDayInInterval(anchorDay)];

  switch (frequency) {
    case PayrollScheduleFrequency.DAILY:
      return { interval: Options.Everyday, every: 1, dayInInterval };
    case PayrollScheduleFrequency.SEMI_MONTHLY:
      return { interval: Options.SemiMonthly, every: 1, dayInInterval };
    case PayrollScheduleFrequency.WEEKLY:
      return {
        interval: Options.Week,
        every: Math.min(every, MAX_WEEK_INTERVAL),
        dayInInterval
      };
    default:
      return { interval: Options.Monthly, every: 1, dayInInterval };
  }
};

const ModalDisablePayroll: React.FC<WSModalCustomProps<boolean>> = props => (
  <WSModal
    title="Are you sure?"
    size="S"
    onClose={() => props.onClose(false)}
    actions={{
      buttons: [
        {
          kind: "Tertiary",
          label: "Cancel",
          onClick: () => props.onClose(false)
        },
        {
          kind: "Primary",
          destructive: true,
          label: "Yes, disable payroll",
          onClick: () => props.onClose(true)
        }
      ]
    }}
  >
    <WSAlert
      theme="warning"
      icon="alert-circle"
      title="Disable payroll and remove upcoming payroll runs"
    >
      If you proceed with disabling payroll now, all upcoming payroll runs will
      be removed. You can always re-enable it and set up your payroll runs from
      scratch.
    </WSAlert>
  </WSModal>
);

const useModalDisablePayroll = () => useWSModalCustom(ModalDisablePayroll);

export const PayrollSettings: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();
  const modalDisablePayroll = useModalDisablePayroll();

  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const querySchedules = queriesPayrollSchedules.listPayrollSchedules.useQuery(
    { filter: { type: { eq: PayrollScheduleType.CONTRACTOR_VENDOR } } },
    // The cadence resource is unimplemented, and a 501 never becomes a 200 — so
    // retrying it just delays the point at which the form can admit it has no
    // saved cadence to show, while spending five requests to learn nothing.
    { retry: false }
  );

  const [updatePayerSettings, updatePayerSettingsMeta] =
    queriesPayers.updateCurrentPayerSettings.useMutation();
  const [updateSchedule, updateScheduleMeta] =
    queriesPayrollSchedules.updatePayrollSchedule.useMutation();

  const payrollSettings = queryPayerSettings.data?.payrollSettings;
  const schedule = querySchedules.data?.data?.[0];

  // A schedule can come back with its pay dates but without the frequency and
  // interval that produced them; inverting the dates recovers the same cadence —
  // see selectorPayrollCadence.
  const derivedCadence = selectorPayrollCadence(schedule?.scheduleDates);

  const setRunsPayroll = useCallback(
    async (runsPayroll: boolean) => {
      await updatePayerSettings(
        { currentPayerSettingsUpdate: { payrollSettings: { runsPayroll } } },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: runsPayroll ? "Payroll enabled" : "Payroll disabled"
            });
          }
        }
      );
    },
    [openSnackbar, updatePayerSettings]
  );

  const handleSubmit = useCallback(
    async (data: FormData) => {
      // The schedule is addressed by its type, not by an id the caller has to
      // hold, so this reaches the payer's contractor schedule whether or not the
      // read above could serve it. There is no create verb at all — a payer with
      // no contractor schedule has nothing this screen can make.
      await updateSchedule(
        {
          type: PayrollScheduleType.CONTRACTOR_VENDOR,
          payrollScheduleUpdate:
            data.interval === Options.Week
              ? {
                  frequency: PayrollScheduleFrequency.WEEKLY,
                  interval: data.every,
                  dayOfWeek: weekDayToScheduleDayOfWeek[data.dayInInterval]
                }
              : {
                  frequency:
                    data.interval === Options.Everyday
                      ? PayrollScheduleFrequency.DAILY
                      : data.interval === Options.SemiMonthly
                        ? PayrollScheduleFrequency.SEMI_MONTHLY
                        : PayrollScheduleFrequency.MONTHLY,
                  interval: 1
                }
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: "Payroll schedule updated"
            });
          }
        }
      );
    },
    [openSnackbar, updateSchedule]
  );

  if (queryPayerSettings.isLoading) {
    return <WSLoader.Spinner />;
  }

  if (queryPayerSettings.error) {
    return (
      <ErrorAlert context="PayrollSettings" error={queryPayerSettings.error} />
    );
  }

  if (!payrollSettings?.runsPayroll) {
    return (
      <WSFlexBox direction="column" alignItems="center" alignContent="center">
        <EmptyState
          mb="M"
          image="NoResults"
          title="No upcoming payroll runs"
          description="Enable payroll to set up payroll runs"
        />

        <WSButton
          onClick={() => setRunsPayroll(true)}
          loading={updatePayerSettingsMeta.isLoading}
        >
          Enable payroll
        </WSButton>

        <ErrorAlert
          mt="M"
          context="PayrollSettings"
          error={updatePayerSettingsMeta.error}
        />
      </WSFlexBox>
    );
  }

  const upcomingRuns = (schedule?.scheduleDates || []).filter(scheduleDate =>
    scheduleDate.cutOffDate
      ? getExactCutOffDate(scheduleDate.cutOffDate) > new Date()
      : false
  );

  return (
    <WSList gap="2XL">
      {!payrollSettings.defaultFundingSource && (
        <WSAlert
          theme="warning"
          icon="alert"
          title="Funding method is missing"
          action={{
            text: "Add funding method",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD, {
                backPath: location.pathname
              });
            }
          }}
        >
          Please add a funding method to enable payroll
        </WSAlert>
      )}

      <WSForm<FormData>
        onSubmit={handleSubmit}
        defaultValues={frequencyToFormData(schedule, derivedCadence)}
        validationSchema={Yup.object().shape({
          interval: Yup.string().oneOf(Object.values(Options)).required(),
          every: Yup.number().when("interval", {
            is: Options.Week,
            then: Yup.number()
              .required("Required")
              .min(1, "Must be at least 1 week")
              .max(
                MAX_WEEK_INTERVAL,
                "Payroll can run every 1 or 2 weeks. Longer intervals aren't supported yet."
              )
          }),
          dayInInterval: Yup.string()
            .oneOf(Object.values(WeekDay))
            .when("interval", {
              is: Options.Week,
              then: Yup.string()
                .oneOf(Object.values(WeekDay))
                .required("Required")
            })
        })}
      >
        {({ setValue }) => (
          <WSList gap="XL">
            <WSElement>
              <WSText.ParagraphSm weight="medium">
                Expected pay date
              </WSText.ParagraphSm>

              {/* Only warn once both sources are gone. With the cadence resource
                  down but schedule dates present, the derivation gives the payer
                  their real setting and there is nothing to caveat; with neither,
                  the radios would otherwise present the "Monthly" fallback as
                  though it were saved, which is a confident lie. */}
              {!!querySchedules.error && !derivedCadence && (
                <WSAlert mt="M" theme="warning" icon="alert">
                  Your payroll cadence can't be loaded right now, so the
                  selection below isn't your saved setting. Saving will still
                  apply the cadence you choose.
                </WSAlert>
              )}

              <RadioGroupWithCustomInputs
                mt="M"
                label="How often should your contractors receive payments?"
                name="interval"
                options={[
                  { value: Options.Monthly, label: "Monthly" },
                  { value: Options.SemiMonthly, label: "Semi-monthly" },
                  { value: Options.Everyday, label: "Everyday" },
                  {
                    value: Options.Week,
                    label: (
                      <WSFlexBox wrap="nowrap" alignItems="center">
                        <WSText.ParagraphSm inline mr="M">
                          Every
                        </WSText.ParagraphSm>
                        {/* The inline fields sit inside a radio label, which
                            stretches to the row width — so each needs its own
                            fixed track or the two inputs span the whole page. */}
                        <WSElement style={{ width: 72, flex: "0 0 auto" }}>
                          <WSForm.Field
                            name="every"
                            component={WSInputNumber}
                            componentProps={{
                              min: 1,
                              max: MAX_WEEK_INTERVAL,
                              onFocus: () => setValue("interval", Options.Week)
                            }}
                          />
                        </WSElement>
                        <WSText.ParagraphSm inline mx="M">
                          week(s) on
                        </WSText.ParagraphSm>
                        <WSElement style={{ width: 180, flex: "0 0 auto" }}>
                          <WSForm.Field
                            name="dayInInterval"
                            component={WSSelect}
                            componentProps={{
                              options: WEEK_DAYS.map(day => ({
                                value: day,
                                label: day
                              })),
                              onFocus: () => setValue("interval", Options.Week)
                            }}
                          />
                        </WSElement>
                      </WSFlexBox>
                    )
                  }
                ]}
              />
            </WSElement>

            <ErrorAlert
              context="PayrollSettings"
              error={updateScheduleMeta.error}
            />
            <ErrorAlert
              context="PayrollSettings"
              error={updatePayerSettingsMeta.error}
            />

            <WSActions
              alignment="left"
              buttons={[
                {
                  label: "Save",
                  loading: updateScheduleMeta.isLoading
                },
                {
                  kind: "Secondary",
                  destructive: true,
                  type: "button",
                  label: "Disable payroll",
                  loading: updatePayerSettingsMeta.isLoading,
                  onClick: async () => {
                    if (await modalDisablePayroll.open({})) {
                      await setRunsPayroll(false);
                    }
                  }
                }
              ]}
            />
          </WSList>
        )}
      </WSForm>

      <WSElement style={{ maxWidth: 400 }}>
        <WSText.Heading5 mb="XL">Upcoming payroll runs</WSText.Heading5>

        <WSTable<RecurrenceScheduleDate>
          tableData={upcomingRuns.map((scheduleDate, index) => ({
            id: String(index),
            data: scheduleDate
          }))}
          columns={[
            {
              config: { header: { text: "Processing date" } },
              renderFunction: ({ data }) => (
                <WSTableCell
                  text={toWSDateString(data.cutOffDate, "monthDayYear")}
                />
              )
            },
            {
              config: { header: { text: "Expected pay date" } },
              renderFunction: ({ data }) => (
                <WSTableCell text={toWSDateString(data.date, "monthDayYear")} />
              )
            }
          ]}
        />
      </WSElement>
    </WSList>
  );
};
